import * as THREE from 'three'
import { Vector2 } from 'three'
import * as ASTAR from '../Utils/astar'
import {DappCoordinatesExpanded} from '../Utils/MatrixPositions'

export default class PathLayer
{
    constructor(scene, data) 
    {
        this.scene = scene
        this.data = data
        
        this.size = this.scene.GRIDsize
        this.center = (this.size - 1) / 2
        
        this.scene.time.on('tick', () =>
        {
            this.update()
        })
    }

    clearArrays() {
        this.lines = []
        this.linesAnimationOffset = []
        this.graph = null
        this.result = []
        this.daappCoords = []
    }

    showLinesExpanded(node) {
        this.node = node
        this.clearArrays()
        this.buildGraphExpanded()
        for (let i = 0; i < this.node.numberOfDapps; i++) {
            this.getPath([6,6], this.daappCoords[i])
            this.drawPath() 
        }
        for (let i = 0; i < this.data.clientNodes.length; i++) {
            const clientNode = this.data.clientNodes[i]
            if (clientNode.parentBlockchainIndex == this.node.myParsedIndex) {
                this.getPath([6,6], clientNode.gridCoords)
                this.drawPath()
            }
        }
    }

    showLinesClient(node) {
        if (this.isHoverActive) return
        this.node = node
        this.clearArrays()
        this.buildGraphClient(node)
        const nodesParentChain = this.data.blockChainNodes[node.parentBlockchainIndex]
        this.getPath(nodesParentChain.gridCoords, node.gridCoords)
        this.drawPath() 
    }

    showHoverLine(node) {
        if (this.isHoverActive) return
        this.node = node
        this.clearArrays()
        this.buildGraphHover()
        const nodesParentChain = this.data.blockChainNodes[node.parentBlockchainIndex]
        if (!nodesParentChain) return
        this.getPath(nodesParentChain.gridCoords, node.gridCoords)
        this.drawPath(true) 
        this.isHoverActive = true
    }

    clearHoverLine() {
        if (!this.isHoverActive) return
        this.clearAllPaths()
        this.isHoverActive = false
    }

    buildGraphHover() {
        let protoGraph = this.emptyProtoGraph()
        for (let i = 0; i < this.data.blockChainNodes.length; i++) {
            const blockchainNode = this.data.blockChainNodes[i]
            protoGraph[blockchainNode.gridCoords[0]][blockchainNode.gridCoords[1]] = 10
        }
        for (let i = 0; i < this.data.clientNodes.length; i++) {
            const clientNode = this.data.clientNodes[i]
            protoGraph[clientNode.gridCoords[0]][clientNode.gridCoords[1]] = 10
        }
        for (let i = 0; i < this.data.offchainNodes.length; i++) {
            const offchainNode = this.data.offchainNodes[i]
            protoGraph[offchainNode.gridCoords[0]][offchainNode.gridCoords[1]] = 10
        }

        this.graph = new ASTAR.Graph(protoGraph)
    }

    buildGraphClient(node) {
        let protoGraph = this.emptyProtoGraph()
        const nodesParentChain = this.data.blockChainNodes[node.parentBlockchainIndex]
        protoGraph[node.gridCoords[0]][node.gridCoords[1]] = 10
        protoGraph[nodesParentChain.gridCoords[0]][nodesParentChain.gridCoords[1]] = 10
        this.graph = new ASTAR.Graph(protoGraph)
    }

    buildGraphExpanded() {

        let protoGraph = this.emptyProtoGraph()

        this.daappCoords = this.getDappCoords()

        //update graph with dapp position weights
        for (let i = 0; i < this.daappCoords.length; i++) {
            protoGraph[this.daappCoords[i][0]][this.daappCoords[i][1]] = 10
        }

        //add blockchain position weight
        protoGraph[this.center][this.center] = 10

        //add client position weights
        for (let i = 0; i < this.data.clientNodes.length; i++) {
            const clientNode = this.data.clientNodes[i]
            if (clientNode.parentBlockchainIndex == this.node.myParsedIndex) {
                protoGraph[clientNode.gridCoords[0]][clientNode.gridCoords[1]] = 10
            }
        }

        this.graph = new ASTAR.Graph(protoGraph)
    }

    emptyProtoGraph() {
        let protoGraph = []
        for (let i = 0; i < this.size; i++) {
            let graphLine = []
            for (let j = 0; j < this.size; j++) {
                graphLine.push(1)
            }
            protoGraph.push(graphLine)
        }
        return protoGraph
    }

    getDappCoords() {
        let daappCoords =[]
        for (let i = 0; i < DappCoordinatesExpanded[this.node.numberOfDapps].length; i++) {
            daappCoords.push([0,0])
            daappCoords[i][0] = DappCoordinatesExpanded[this.node.numberOfDapps][i][0]
            daappCoords[i][1] = DappCoordinatesExpanded[this.node.numberOfDapps][i][1]
        }

        //convert dapps from relative to absolute coords
        for (let i = 0; i < DappCoordinatesExpanded[this.node.numberOfDapps].length; i++) {
            daappCoords[i][0] = this.center - DappCoordinatesExpanded[this.node.numberOfDapps][i][0] * 2
            daappCoords[i][1] = this.center - DappCoordinatesExpanded[this.node.numberOfDapps][i][1] * 2
        }
        return daappCoords
    }

    getPath(StartCoords, EndCoords) {
        var start = this.graph.grid[StartCoords[0]][StartCoords[1]];
        var end = this.graph.grid[EndCoords[0]][EndCoords[1]];
        var result = ASTAR.astar.search(this.graph, start, end);
        result.unshift(new Vector2(StartCoords[0], StartCoords[1]))
        this.result = result
    }

    drawPath(singlemode) {
        const points = [];
        const squareSize = this.scene.STside / this.scene.GRIDsize
        for (let i = 0; i < this.result.length; i++) {
            points.push(new THREE.Vector3(-this.scene.STside * 0.5 + this.result[i].x * squareSize + squareSize * 0.5, this.scene.GRIDposition, 
            this.scene.STside * 0.5 - this.result[i].y * squareSize  - squareSize * 0.5))
        }
        const pointsLast = points.length - 1
        points.push(new THREE.Vector3(points[pointsLast].x, points[pointsLast].y + this.scene.GRIDsquare*0.25, points[pointsLast].z))

        const material = new THREE.LineDashedMaterial( { 
            color: "#4d87fa"
         } )
        material.transparent = true
        material.opacity = 0.7
        let offset = Math.random() * Math.PI*2
        if (singlemode) offset = this.scene.time.elapsed
        const geometry = new THREE.BufferGeometry().setFromPoints( points );
        const line = new THREE.Line( geometry, material );
        this.scene.add(line)
        this.lines.push(line)
        this.linesAnimationOffset.push(offset)
    }

    clearAllPaths() {
        if (!this.lines) return
        for (let i = 0; i < this.lines.length; i++) {
            this.scene.remove(this.lines[i])
        }
        this.lines = []
        this.linesAnimationOffset = []
    }

    update() {
        if (!this.lines) return
        for (let i = 0; i < this.lines.length; i++) {
            const element = this.lines[i];
            element.material.opacity = 1.1 + 0.3*Math.sin(this.linesAnimationOffset[i] + this.scene.time.elapsed*0.0016)
        }
    }
}